import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { CpuChipIcon, LockClosedIcon, UserIcon, GlobeAltIcon } from '@heroicons/vue/20/solid'
import {
  Environment,
  ProjectAIType,
  UILanguage,
  UserRoleId,
  WidgetVisualPosition
} from '../enums.ts'
import { isEmpty } from 'lodash-es'
import type { TranslationFunction } from '@/locales/types.ts'
import type { Project, ProjectSettingsTab } from '../components/projects/interfaces.ts'
import type { NavigationItem } from '@/components/interfaces/header.interfaces.ts'
import type { NavigateLinkItem } from '@/components/interfaces/footer.interface.ts'
import { DEFAULT_ENVIRONMENT } from '@/config/env.const.ts'

export const ASSISTANT_AI_FILES_PREFIX = '/files/ai_assistant_files/'
export const WIDGET_ASSISTANT_AVATAR_MAX_FILE_SIZE_KB = 512
export const USER_TYPING_MESSAGE_THROTTLE_DELAY_MS = 1500
export const CUSTOMER_TYPING_MESSAGE_RESET_ANIMATION_DELAY_MS = 2200
export const MINIMAL_COMFORT_ADMIN_WIDTH_PX = 768
export const WEBSOCKET_RECONNECT_INTERVAL_MS = 3000
export const CHATS_PAGE_SIZE = 30
export const CHATS_DEFAULT_PAGE_NUMBER_ON_LOAD = 1
export const DAYS_IN_WEEK = 7
export const LOCAL_STORAGE_CROWDY_API_ENV_KEY = 'CROWDY_API_ENV'
export const DEFAULT_LANGUAGE: UILanguage = UILanguage.English
export const CONTACT_MAIL: string = 'info@crowdy.ai'
export const CONTACT_TELEPHONE: string = '+372 5820 5605'
export const SocialLink = Object.freeze({
  CONTACT: 'https://crowdy.ai/contact/',
  INSTAGRAM: 'https://www.instagram.com/crowdy.ai/',
  LINKED_IN: 'https://www.linkedin.com/company/crowdy-ai-bot/',
  YOUTUBE: 'https://www.youtube.com/@Crowdy_ai',
  FACEBOOK: 'https://www.facebook.com/companyinestonia/',
  X: 'https://x.com/Crowdy_ai',
})

export function getProjectsSettingsTabs(
  projectKey: string,
  route: RouteLocationNormalizedLoaded,
  t: TranslationFunction
  // TODO: User root store
): ProjectSettingsTab[] {
  if (isEmpty(projectKey)) {
    return []
  }

  return [
    {
      name: t('settings.tabs.assistant'),
      href: `/${projectKey}/settings#assistant`,
      icon: UserIcon,
      current: route.hash === '#assistant' || route.hash === ''
    },
    // {
    //   name: t('settings.tabs.visual'),
    //   href: `/${projectKey}/settings#visual`,
    //   icon: EyeDropperIcon,
    //   current: route.hash === '#visual'
    // },
    {
      name: t('settings.tabs.security'),
      href: `/${projectKey}/settings#security`,
      icon: LockClosedIcon,
      current: route.hash === '#security'
    },
    {
      name: t('settings.tabs.ai'),
      href:  `/${projectKey}/settings#ai`,
      icon: CpuChipIcon,
      current: route.hash === '#ai'
    },
    {
      name: t('settings.tabs.integration'),
      href:  `/${projectKey}/settings#integration`,
      icon: GlobeAltIcon,
      current: route.hash === '#integration'
    },
    // {
    //   name: t('settings.tabs.integrations'),
    //   href: `/${projectKey}/settings#integrations`,
    //   icon: LinkIcon,
    //   current: route.hash === '#integrations'
    // },
  ]
}

export function getProjectAITypes (t: TranslationFunction): {
  id: ProjectAIType
  disabled: boolean
  title: string
  description: string
}[] {
  return [
    {
      id: ProjectAIType.ChatGPTRegular,
      disabled: false,
      title: t('settings.ai.chatGptRegularApi'),
      description: t('settings.ai.chatGptRegularApiDescription'),
    },
    {
      id: ProjectAIType.ChatGPTResponsesAPI,
      disabled: false,
      title: t('settings.ai.chatGptResponsesApi'),
      description: t('settings.ai.chatGptResponsesApiDescription'),
    },
  ]
}

export function getVisualPositionTypes(): {
  id: WidgetVisualPosition
  title: string
}[] {
  return [
    { id: WidgetVisualPosition.Left, title: 'On the left' },
    { id: WidgetVisualPosition.Right, title: 'On the right' },
  ]
}

export function getNavigationLanguageMenuItems (): {
  name: string
  code: UILanguage
  href: string
  imgUrl: string
}[] {
  return [
    { name: 'Eesti',
      code: UILanguage.Estonian,
      href: '#',
      imgUrl: DEFAULT_ENVIRONMENT === Environment.Production
        ? '/admin/images/flags_squared/estonia.png'
        : '/images/flags_squared/estonia.png'
    },
    { name: 'English',
      code: UILanguage.English,
      href: '#',
      imgUrl: DEFAULT_ENVIRONMENT === Environment.Production
        ? '/admin/images/flags_squared/great_britain.png'
        : '/images/flags_squared/great_britain.png'
    },
    {
      name: 'Русский',
      code: UILanguage.Russian,
      href: '#',
      imgUrl: DEFAULT_ENVIRONMENT === Environment.Production
        ? '/admin/images/flags_squared/russia.png'
        : '/images/flags_squared/russia.png'
    },
    {
      name: 'Español',
      code: UILanguage.Spanish,
      href: '#',
      imgUrl: DEFAULT_ENVIRONMENT === Environment.Production
        ? '/admin/images/flags_squared/spain.png'
        : '/images/flags_squared/spain.png'
    },
  ]
}

export function getNavigationUserMenuItems (t: TranslationFunction): {
  name: string
  href: string
}[] {
  return [
    { name: t('navigation.profile'), href: '#' },
    { name:  t('navigation.signOut'), href: '/logout' },
  ]
}

export function getBuildHeaderNavigation(
  project: Project | null,
  roleId: UserRoleId,
  route: RouteLocationNormalizedLoaded,
  t: TranslationFunction
): NavigationItem[] {
  const projectKey = project?.key ?? ''

  const isSuperUserOrAdministrator = [UserRoleId.SuperUser, UserRoleId.Administrator].includes(roleId)

  return [
    {
      name: t('navigation.chats'),
      href: `/${projectKey}/chats`,
      current: route.path.includes('/chats'),
      disabled: isEmpty(projectKey),
      visible: (
        [
          UserRoleId.SystemUser,
          UserRoleId.SuperUser,
          UserRoleId.Administrator,
          UserRoleId.ProjectOwner,
          UserRoleId.CustomerSupport,
        ] as UserRoleId[]
      ).includes(roleId),
    },
    {
      name: t('navigation.settings'),
      href: `/${projectKey}/settings`,
      current: route.path.includes('/settings'),
      disabled: isEmpty(projectKey),
      visible: (
        [
          UserRoleId.SystemUser,
          UserRoleId.SuperUser,
          UserRoleId.Administrator,
          UserRoleId.ProjectOwner,
          UserRoleId.CustomerSupport,
        ] as UserRoleId[]
      ).includes(roleId),
    },
    {
      name: t('navigation.analytics'),
      href: projectKey.length > 0 ? `/${projectKey}/analytics` : '/analytics', // TODO: Different analytics for project owners and superusers/administrator
      current: route.path.includes('/analytics'),
      disabled: isEmpty(projectKey) && !isSuperUserOrAdministrator,
      visible: (
        [
          UserRoleId.SystemUser,
          UserRoleId.SuperUser,
          UserRoleId.Administrator,
          UserRoleId.ProjectOwner,
          UserRoleId.CustomerSupport,
        ] as UserRoleId[]
      ).includes(roleId),
    },
    {
      name: t('navigation.users'),
      href: '/users',
      current: route.path.includes('/users'),
      disabled: isEmpty(projectKey) && !isSuperUserOrAdministrator,
      visible: (
        [
          UserRoleId.SystemUser,
          UserRoleId.SuperUser,
          UserRoleId.Administrator,
        ] as UserRoleId[]
      ).includes(roleId),
    },
    {
      name: t('navigation.projects'), // TODO: Only for superusers
      href: '/projects',
      current: route.path.includes('/projects'),
      disabled: isEmpty(projectKey) && !isSuperUserOrAdministrator,
      visible: (
        [
          UserRoleId.SystemUser,
          UserRoleId.SuperUser,
          UserRoleId.Administrator,
        ] as UserRoleId[]
      ).includes(roleId),
    },
    {
      name: 'Playground',
      href: projectKey.length > 0 ? `/${projectKey}/playground` : '',
      current: route.path.includes('/playground'),
      disabled: isEmpty(projectKey),
      visible: DEFAULT_ENVIRONMENT != Environment.Production
    },

  ].filter((item) => item.visible)
}

export function getCooperationList(t: TranslationFunction): NavigateLinkItem[] {
  return [
    { text: 'FAQ', href: 'https://crowdy.ai/faq/' },
    { text: 'GDPR', href: 'https://crowdy.ai/gdpr/' },
    { text: t('footer.privacyPolicy'), href: 'https://crowdy.ai/privacy-policy/' },
    { text: t('footer.termsConditions'), href: 'https://crowdy.ai/terms-and-conditions/' },
    { text: t('footer.contacts'), href: 'https://crowdy.ai/contact/' },
  ]
}

export function getAboutList(t: TranslationFunction): NavigateLinkItem[] {
  return [
    { text: t('footer.blog'), href: 'https://crowdy.ai/blog/' },
    { text: t('footer.aboutCrowdy'), href: 'https://crowdy.ai/about-crowdy-ai/' },
    { text: t('footer.joinOurTeam'), href: 'https://crowdy.ai/join-our-team/' },
    { text: t('footer.forPartners'), href: 'https://crowdy.ai/for-partners/' },
    { text: t('footer.forInvestors'), href: 'https://crowdy.ai/for-investors/' },
  ]
}

export function getAnalyticsUrlByProjectKey (projectKey: string): string {
  return `/${projectKey}/analytics`
}
